import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const ENTITY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'leads', label: 'Leads' },
  { id: 'jobs', label: 'Jobs' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'deals', label: 'Deals' },
  { id: 'accounts', label: 'Accounts' }
];

const ENTITY_COLORS = {
  leads: 'bg-pink-100 text-pink-700',
  jobs: 'bg-blue-100 text-blue-700',
  contacts: 'bg-green-100 text-green-700',
  tasks: 'bg-yellow-100 text-yellow-700',
  deals: 'bg-purple-100 text-purple-700',
  accounts: 'bg-orange-100 text-orange-700',
  templates: 'bg-gray-100 text-gray-700'
};

const daysAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  return Math.floor(diff / 86400000);
};

const TrashPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const { profile } = useAuth();

  const fetchDeletedItems = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const tables = [
        { name: 'leads', labelField: 'name', type: 'leads' },
        { name: 'jobs', labelField: 'title', type: 'jobs' },
        { name: 'contacts', labelField: null, type: 'contacts' },
        { name: 'tasks', labelField: 'title', type: 'tasks' },
        { name: 'deals', labelField: 'name', type: 'deals' },
        { name: 'accounts', labelField: 'name', type: 'accounts' },
        { name: 'templates', labelField: 'name', type: 'templates' }
      ];

      const results = await Promise.all(
        tables?.map(async (t) => {
          const { data } = await supabase?.from(t?.name)?.select('id, created_at, deleted_at' + (t?.labelField ? `, ${t?.labelField}` : ', first_name, last_name'))?.eq('org_id', profile?.org_id)?.not('deleted_at', 'is', null);
          return (data || [])?.map(item => ({
            ...item,
            _type: t?.type,
            _table: t?.name,
            _label: t?.labelField ? item?.[t?.labelField] : `${item?.first_name || ''} ${item?.last_name || ''}`?.trim()
          }));
        })
      );

      const allItems = results?.flat()?.sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at));
      setItems(allItems);
    } catch (err) {
      toast?.error('Failed to load trash');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchDeletedItems(); }, [fetchDeletedItems]);

  const handleRestore = async (item) => {
    try {
      await supabase?.from(item?._table)?.update({ deleted_at: null })?.eq('id', item?.id);
      toast?.success(`${item?._label} restored!`);
      fetchDeletedItems();
    } catch (err) {
      toast?.error('Failed to restore');
    }
  };

  const handlePermanentDelete = async (item) => {
    if (!window.confirm(`Permanently delete "${item?._label}"? This cannot be undone.`)) return;
    try {
      await supabase?.from(item?._table)?.delete()?.eq('id', item?.id);
      toast?.success('Permanently deleted');
      fetchDeletedItems();
    } catch (err) {
      toast?.error('Failed to delete permanently');
    }
  };

  const handleEmptyTrash = async () => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000)?.toISOString();
    const oldItems = items?.filter(i => new Date(i.deleted_at) < new Date(thirtyDaysAgo));
    if (oldItems?.length === 0) {
      toast('No items older than 30 days to delete');
      return;
    }
    if (!window.confirm(`Permanently delete ${oldItems?.length} items older than 30 days?`)) return;
    setEmptyingTrash(true);
    try {
      const byTable = {};
      oldItems?.forEach(item => {
        if (!byTable?.[item?._table]) byTable[item._table] = [];
        byTable?.[item?._table]?.push(item?.id);
      });
      await Promise.all(
        Object.entries(byTable)?.map(([table, ids]) =>
          supabase?.from(table)?.delete()?.in('id', ids)
        )
      );
      toast?.success(`Emptied ${oldItems?.length} old items from trash`);
      fetchDeletedItems();
    } catch (err) {
      toast?.error('Failed to empty trash');
    } finally {
      setEmptyingTrash(false);
    }
  };

  const filteredItems = activeTab === 'all' ? items : items?.filter(i => i?._type === activeTab);

  return (
    <>
      <Helmet><title>Trash - Wilcart CRM</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Trash</h1>
                <p className="text-muted-foreground mt-1">{items?.length} deleted items</p>
              </div>
              {items?.length > 0 && (
                <Button variant="destructive" onClick={handleEmptyTrash} loading={emptyingTrash} className="mt-4 sm:mt-0">
                  <Icon name="Trash2" size={16} className="mr-2" />
                  Empty Trash (30+ days)
                </Button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 mb-6 border-b border-border">
              {ENTITY_TABS?.map(tab => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab?.id
                      ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
                  }`}>
                  {tab?.label}
                  {tab?.id !== 'all' && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      ({items?.filter(i => i?._type === tab?.id)?.length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="space-y-3">{[1,2,3,4,5]?.map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
            ) : filteredItems?.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Trash2" size={36} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">Trash is empty</h3>
                <p className="text-muted-foreground">Deleted items will appear here. You can restore or permanently delete them.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredItems?.map(item => (
                  <div key={`${item?._type}-${item?.id}`}
                    className="bg-card border border-border rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${ENTITY_COLORS?.[item?._type] || 'bg-gray-100 text-gray-700'}`}>
                        <Icon name={
                          item?._type === 'leads' ? 'UserPlus' :
                          item?._type === 'jobs' ? 'Briefcase' :
                          item?._type === 'contacts' ? 'User' :
                          item?._type === 'tasks' ? 'CheckSquare' :
                          item?._type === 'deals' ? 'Target' :
                          item?._type === 'accounts' ? 'Building2' : 'FileText'
                        } size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item?._label || 'Unnamed'}</p>
                        <div className="flex items-center space-x-2">
                          <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium capitalize ${ENTITY_COLORS?.[item?._type] || 'bg-gray-100 text-gray-700'}`}>
                            {item?._type}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Deleted {daysAgo(item?.deleted_at)} days ago
                          </span>
                          {daysAgo(item?.deleted_at) >= 30 && (
                            <span className="text-xs text-red-600 font-medium">Will be auto-deleted</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                      <button onClick={() => handleRestore(item)}
                        className="px-3 py-1.5 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded-lg font-medium transition-colors flex items-center">
                        <Icon name="RotateCcw" size={12} className="mr-1" />
                        Restore
                      </button>
                      <button onClick={() => handlePermanentDelete(item)}
                        className="px-3 py-1.5 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors flex items-center">
                        <Icon name="Trash2" size={12} className="mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default TrashPage;
